import clsx from 'clsx';
import { differenceInCalendarDays } from 'date-fns';
import type { ClientVacationPeriod } from '../../../../../models/vacationClientView.types';
import { VacationAvatar } from '../../../../VacationDpTable/components/VacationAvatar';
import { formatPeriodRange } from '../../../vacationClientPanel.definitions';
import type { ConflictPersonItem } from '../clientPeriodCard.types';
import { CONFLICT_TYPE_BADGE, MINI_BAR_HEIGHT, getPersonBadgeLabel, getPersonTooltip } from '../clientPeriodCard.definitions';
import { CALENDAR_BAR_CLASSES, barToneStyle } from '../../../vacationClientPanel.definitions';
import { CollaboratorNameLink } from '../../CollaboratorNameLink';

interface ConflictPersonRowProps {
    person: ConflictPersonItem;
    period: ClientVacationPeriod;
}

function OverlapMiniBar({ person, period }: { person: ConflictPersonItem; period: ClientVacationPeriod }) {
    const totalDays = differenceInCalendarDays(period.endDate, period.startDate) + 1;

    return (
        <div className={clsx('relative bg-raro-gray-200 rounded-full overflow-hidden mt-2', MINI_BAR_HEIGHT)}>
            {person.colleagueRanges.map((range) => {
                const rangeStart = range.start < period.startDate ? period.startDate : range.start;
                const rangeEnd = range.end > period.endDate ? period.endDate : range.end;
                if (rangeStart > rangeEnd) return null;

                const startOffset = differenceInCalendarDays(rangeStart, period.startDate);
                const rangeWidth = differenceInCalendarDays(rangeEnd, rangeStart) + 1;
                const startPct = (startOffset / totalDays) * 100;
                const widthPct = (rangeWidth / totalDays) * 100;

                return (
                    <div
                        key={`${range.start.getTime()}-${range.end.getTime()}`}
                        className="absolute inset-y-0 bg-raro-gray-100"
                        style={{ left: `${startPct}%`, width: `${widthPct}%` }}
                    />
                );
            })}

            {person.overlapRanges.map((range) => {
                const overlapStartOffset = differenceInCalendarDays(range.start, period.startDate);
                const overlapWidth = differenceInCalendarDays(range.end, range.start) + 1;
                const overlapPct = (overlapStartOffset / totalDays) * 100;
                const overlapWidthPct = (overlapWidth / totalDays) * 100;

                return (
                    <div
                        key={`${range.start.getTime()}-${range.end.getTime()}`}
                        className={clsx('absolute inset-y-0 rounded-full', CALENDAR_BAR_CLASSES[person.type as keyof typeof CALENDAR_BAR_CLASSES])}
                        style={{ ...{ left: `${overlapPct}%`, width: `${overlapWidthPct}%` }, ...barToneStyle(person.type) }}
                    />
                );
            })}
        </div>
    );
}

export function ConflictPersonRow({ person, period }: ConflictPersonRowProps) {
    const badgeLabel = getPersonBadgeLabel(person);
    const badgeColor = person.type ? (CONFLICT_TYPE_BADGE[person.type] || '') : '';

    const rangesDisplay = person.colleagueRanges
        .map(r => formatPeriodRange(r.start, r.end))
        .join(' · ');

    const tooltip = getPersonTooltip(person, { name: period.collaboratorName, segmentCount: period.segments.length });

    return (
        <div className="space-y-2">
            <div className="flex items-center gap-3">
                <VacationAvatar name={person.name} photoUrl={person.photoUrl} />
                <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-raro-gray-900">
                        <CollaboratorNameLink collaboratorId={person.collaboratorId} name={person.name} />
                    </p>
                    <p className="text-xs text-raro-gray-500 truncate" title={rangesDisplay}>{rangesDisplay}</p>
                </div>
                <span className={clsx('text-xs font-semibold px-2 py-1 rounded whitespace-nowrap', badgeColor)}>
                    {badgeLabel}
                </span>
            </div>
            <div title={tooltip}>
                <OverlapMiniBar person={person} period={period} />
            </div>
        </div>
    );
}
